import { useMemo } from "react";

import { CAMEROON_REGION_PATHS, CAMEROON_VIEWBOX } from "@/data/cameroon-geo";
import type { DeliveryRegion } from "@/lib/logistics/delivery-api";

interface CameroonMapProps {
	regions: DeliveryRegion[];
	selected: string | null;
	onSelect: (code: string) => void;
	/** Masque les libellés (utile en petit format). */
	compact?: boolean;
}

type RegionState = "selected" | "served" | "empty" | "inactive";

/** Couleurs d'aplat par état — palette Akwaba, AUCUN dégradé. */
const FILL: Record<RegionState, string> = {
	selected: "var(--a-terracotta)",
	served: "var(--a-forest-soft)",
	empty: "var(--a-ochre-soft)",
	inactive: "var(--a-surface-2)",
};
const STROKE: Record<RegionState, string> = {
	selected: "var(--a-terra-deep)",
	served: "var(--a-forest)",
	empty: "var(--a-ochre)",
	inactive: "var(--a-border-strong)",
};
const LABEL: Record<RegionState, string> = {
	selected: "#fff",
	served: "var(--a-forest)",
	empty: "var(--a-ink-soft)",
	inactive: "var(--a-ink-mute)",
};

/**
 * Carte SVG interactive des 10 régions du Cameroun. Chaque région se colore
 * selon son état de couverture (desservie / vide / inactive / sélectionnée) et
 * se clique pour l'édition. Tracés générés depuis geoBoundaries (CC BY 3.0).
 */
export function CameroonMap({
	regions,
	selected,
	onSelect,
	compact,
}: CameroonMapProps) {
	const byCode = useMemo(
		() => new Map(regions.map((r) => [r.code, r])),
		[regions],
	);

	const stateOf = (code: string): RegionState => {
		if (selected === code) return "selected";
		const r = byCode.get(code);
		if (!r || !r.isActive) return "inactive";
		return r.activeCityCount > 0 ? "served" : "empty";
	};

	return (
		<div className="cmr-wrap">
			<style>{`
        .cmr-wrap { width: 100%; }
        .cmr-svg { width: 100%; height: auto; display: block; }
        .cmr-region { cursor: pointer; transition: fill 0.14s, stroke 0.14s, filter 0.14s; }
        .cmr-region:hover { filter: brightness(0.95); }
        .cmr-region:focus { outline: none; }
        .cmr-region.is-selected { filter: none; }
        .cmr-label { pointer-events: none; font-family: var(--a-font); }
      `}</style>
			<svg
				className="cmr-svg"
				viewBox={CAMEROON_VIEWBOX}
				role="img"
				aria-label="Carte des régions de livraison du Cameroun"
			>
				{/* Tracés des régions */}
				{CAMEROON_REGION_PATHS.map((rp) => {
					const st = stateOf(rp.code);
					return (
						<path
							key={rp.code}
							d={rp.d}
							className={`cmr-region${st === "selected" ? "is-selected" : ""}`}
							fill={FILL[st]}
							stroke={STROKE[st]}
							strokeWidth={st === "selected" ? 5 : 2}
							strokeLinejoin="round"
							onClick={() => onSelect(rp.code)}
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									onSelect(rp.code);
								}
							}}
						>
							<title>{rp.name}</title>
						</path>
					);
				})}

				{/* Libellés (nom + nb de villes) */}
				{!compact &&
					CAMEROON_REGION_PATHS.map((rp) => {
						const st = stateOf(rp.code);
						const r = byCode.get(rp.code);
						return (
							<g key={`l-${rp.code}`} className="cmr-label">
								<text
									x={rp.cx}
									y={rp.cy - 4}
									textAnchor="middle"
									fontSize={21}
									fontWeight={700}
									fill={LABEL[st]}
								>
									{rp.name}
								</text>
								<text
									x={rp.cx}
									y={rp.cy + 20}
									textAnchor="middle"
									fontSize={17}
									fill={
										st === "selected"
											? "rgba(255,255,255,0.85)"
											: "var(--a-ink-mute)"
									}
								>
									{r && r.activeCityCount > 0
										? `${r.activeCityCount} ville${r.activeCityCount > 1 ? "s" : ""}`
										: "—"}
								</text>
							</g>
						);
					})}
			</svg>
		</div>
	);
}
